/**
 * Short share links: `/s/<id>` in, a full view restored behind that same path.
 *
 * A shared view used to travel as its whole query string - six hundred
 * characters that wrap in Teams, break in email, and freeze the wire format of
 * the day they were sent. A short link is the same view stored server-side
 * under a twelve-character content hash, so the link is small, the same view
 * shared twice is the same link, and the parameters can be upgraded later
 * because the backend owns them rather than the recipient's clipboard.
 *
 * **The long query format is not going anywhere.** It remains the internal wire
 * format used by `restoreFromUrl`, saved views, tiles, Assistant proposals, and
 * legacy links. Resolving a short link returns that query to boot without
 * writing it into the address bar.
 *
 * With no database the backend has nowhere to put a snapshot and says so; Share
 * then builds a legacy long URL for the clipboard. Sharing degrades to ugly,
 * never to broken, while ordinary navigation remains clean.
 *
 * `/c/<id>` is the catalog's equivalent and expands the same way, with one
 * deliberate difference in meaning: a snapshot is frozen, while a catalog link
 * is resolved fresh on every open and therefore follows the owner's edits. That
 * is why both exist - "the view I was looking at" and "that chart" are not the
 * same promise.
 */

import { createShareLink, getChart, resolveShareLink } from '../api/endpoints.ts';
import { showToast } from '../ui/toast.ts';
import { buildQueryString } from './url-sync.ts';
import { withCurrentContext } from './presets.ts';

/** `/s/fujx66uq4i5a` - twelve lowercase base32 characters, as the backend mints them. */
const SHARE_PATH = /^\/s\/([a-z2-7]{12})\/?$/;

/** `/c/<uuid>` - a saved catalog chart, by its id. */
const CHART_PATH = /^\/c\/([0-9a-f-]{36})\/?$/i;

/** The id in the current address bar, if this load came from a short link. */
export function shareIdFromLocation(): string | null {
  return SHARE_PATH.exec(window.location.pathname)?.[1] ?? null;
}

/** The chart id in the current address bar, if this load came from `/c/<id>`. */
export function chartIdFromLocation(): string | null {
  return CHART_PATH.exec(window.location.pathname)?.[1] ?? null;
}

export interface ResolvedLocationView {
  query: string;
  /** Keep the permalink visible for the initial restore. */
  visibleUrl: string;
}

function currentPermalink(): string {
  const flags = new URLSearchParams(window.location.search).get('ff');
  return `${window.location.pathname}${flags ? `?ff=${encodeURIComponent(flags)}` : ''}`;
}

/** Resolve `/s/<id>` and `/c/<id>` without exposing their stored query. */
export async function resolveLocationView(): Promise<ResolvedLocationView | null> {
  const snapshotId = shareIdFromLocation();
  if (snapshotId) {
    try {
      const resolved = await resolveShareLink(snapshotId);
      return { query: resolved.query, visibleUrl: currentPermalink() };
    } catch {
      // An expired, revoked or mistyped link. Land on the empty app rather than
      // on a URL that will 404 again on every reload, and say why.
      window.history.replaceState(null, '', '/');
      showToast('error', 'That shared link is no longer available');
      return null;
    }
  }

  const chartId = chartIdFromLocation();
  if (chartId) {
    try {
      const chart = await getChart(chartId);
      if (chart.pin_ship === undefined && chart.pin_time === undefined && chart.bind_context) {
        return { query: chart.query, visibleUrl: currentPermalink() };
      }
      const query = new URLSearchParams(withCurrentContext(chart.query));
      for (const [key, value] of Object.entries(chart.context ?? {})) query.set(key, value);
      return { query: query.toString(), visibleUrl: currentPermalink() };
    } catch {
      // 404 covers both "deleted" and "never shared with you" - the API does not
      // distinguish, because whether a chart exists is itself information about
      // somebody else's catalog.
      window.history.replaceState(null, '', '/');
      showToast('error', 'That chart is not available to you');
      return null;
    }
  }
  return null;
}

/**
 * A short absolute URL for the current in-memory view.
 *
 * Returns null when the backend has no database (or is down), which is the
 * caller's cue to share the long URL instead.
 */
export async function shortenCurrentView(
  opts: { pinContext?: boolean; title?: string } = {},
): Promise<string | null> {
  const query = buildQueryString();
  if (!query) return null;
  try {
    const link = await createShareLink(query, opts);
    return `${window.location.origin}${link.path}`;
  } catch {
    return null;
  }
}

/** Portable fallback for deployments without short-link storage. It is built
 * only when Share is used; the ordinary address bar remains clean. */
export function longCurrentViewUrl(): string {
  const query = buildQueryString();
  return query
    ? `${window.location.origin}/?${query}`
    : `${window.location.origin}/`;
}
